import * as React from 'react';
import classnames from 'classnames';
import { isNil, sortBy } from 'lodash';

import ActionLink from 'components/buttons/action-link/ActionLink';
import MediaPreview from 'components/MediaPreview';
import CampaignEntityAvatar from 'components/entity/entity-avatar/CampaignEntityAvatar';
import CampaignEntityName from 'components/entity/entity-name/CampaignEntityName';
import PageSection from 'components/page/page-section/PageSection';
import { withModal } from 'components/modal/ModalHOC';
import RichTextEditor from 'components/inputs/rich-text-editor/RichTextEditor';
import ActionButton from 'components/buttons/action-button/ActionButton';
import HelpNote from 'components/help-note/HelpNote';

import CampaignEditModal from '../CampaignEditModal';

import { getCampaignManagersPreview } from 'utils/campaign';
import { getEntityFromProfile } from 'utils/view';
import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import { prepareCampaignAssetForMediaPreview, prepareCampaignAssetsForGallery } from 'utils/campaignAsset';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { isEmptyEditorText } from 'utils/editor';

import './campaign-card.scss';

import type { BrandProfile, Campaign, CampaignOverview } from 'types';

applyTranslations(require('./campaign-card.i18n.json'));

const DivWithModal = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);
const ActionButtonWithModalEdit = withModal(ActionButton, {
  modalContent: CampaignEditModal
});

type Props = {
  campaign: Campaign,
  campaignOverview?: CampaignOverview,
  brandProfile: BrandProfile,
  isForBrand: boolean,
  disableEditButton?: boolean,
  showExpanded: boolean,
  layout: 'vertical' | 'horizontal',
  view: 'compact' | 'normal',
  onDownloadOffer?: () => void
};

type State = {
  isCollapsed: boolean,
  isExpandable: boolean
};

class CampaignCard extends React.PureComponent<Props, State> {
  static defaultProps = {
    view: 'normal',
    isForBrand: false,
    showExpanded: false
  };

  state = {
    isCollapsed: !this.props.showExpanded,
    isExpandable: true
  };

  campaignCardContentRef: {
    current: null | HTMLDivElement
  } = React.createRef();

  componentDidMount() {
    this.updateExpandableStatus();
  }

  handleDownloadOffer = () => {
    const { onDownloadOffer } = this.props;

    if (onDownloadOffer) {
      onDownloadOffer();
    }
  };

  toggleCollapsed = () => {
    this.setState(state => ({
      isCollapsed: !state.isCollapsed
    }));
  };

  updateExpandableStatus() {
    const currentDOMRef = this.campaignCardContentRef.current;
    if (!currentDOMRef) {
      return;
    }

    const height = currentDOMRef.getBoundingClientRect().height || Infinity;
    this.setState({
      isExpandable: height > 245 // height of campaign-card--collapsed without extra margin
    });
  }

  renderHeader() {
    const { campaign, campaignOverview, brandProfile, isForBrand, disableEditButton, view, onDownloadOffer } = this.props;

    const brandEntity = getEntityFromProfile(brandProfile);
    const isCompactView = view === 'compact';

    return (
      <div className="campaign-card__header">
        <div className="campaign-card__title-section">
          {
            !isCompactView && (
              <div className="campaign-card__avatar">
                <CampaignEntityAvatar
                  campaign={campaign}
                  form="square"
                  size="bigger"
                  disabled={!isForBrand}
                />
              </div>
            )
          }
          <div className="text-overflow-ellipsis" data-hj-suppress>
            <div className="campaign-card__title">
              <CampaignEntityName
                campaign={campaign}
                disabled={!isForBrand}
                size="inherit"
                className="campaign-card__campaign-title text-overflow-ellipsis"
              />
              {
                !isNil(brandEntity?.displayName) && (
                  <React.Fragment>
                    <span className="campaign-card__by-label">{__t('campaign_card.by_label')}</span>
                    <span
                      className="campaign-card__by-brand text-overflow-ellipsis"
                      title={brandEntity?.displayName}
                    >
                      <ActionLink
                        size="inherit"
                        link={brandEntity?.url}
                      >
                        {brandEntity?.displayName}
                      </ActionLink>
                    </span>
                  </React.Fragment>
                )
              }
            </div>
            {
              !isCompactView && (
                <div className="campaign-card__date">
                  <div className="campaign-card__date-start">
                    <span className="campaign-card__date-label">
                      {__t('campaign_card.start_date_label')}:&nbsp;
                    </span>
                    <span>{getDisplayDate(campaign.startDate)}</span>
                  </div>
                  <div className="campaign-card__date-end">
                    <span className="campaign-card__date-label">
                      {__t('campaign_card.end_date_label')}:&nbsp;
                    </span>
                    <span>{getDisplayDate(campaign.endDate)}</span>
                  </div>
                </div>
              )
            }
          </div>
        </div>
        {isForBrand && !disableEditButton && (
          <div className="campaign-card__actions-section">
            <ActionButtonWithModalEdit
              color="secondary"
              width="maximum"
              modalContentProps={{
                brandProfileId: brandProfile.id,
                campaignId: campaign.id,
                campaignOverview,
                triggerType: 'button'
              }}
            >
              {__t('campaign_card.edit_campaign_cta_title')}
            </ActionButtonWithModalEdit>
            {
              !isCompactView && (
                <HelpNote message={__t('campaign_card.edit_campaign_cta_description')} />
              )
            }
          </div>
        )}
        {
          onDownloadOffer && (
            <div className="campaign-card__actions-section">
              <ActionButton
                color="secondary"
                width="maximum"
                onClick={this.handleDownloadOffer}
              >
                {__t('campaign_card.download_brief')}
              </ActionButton>
            </div>
          )
        }
      </div>
    );
  }

  render() {
    const { campaign, layout, view } = this.props;
    const { isCollapsed, isExpandable } = this.state;

    const inspirationalMaterial = sortBy(campaign.assets || [], 'id');
    const galleryItems = prepareCampaignAssetsForGallery(inspirationalMaterial);

    const campaignDescription = campaign.description;
    const campaignAdditionalRequirementsTemplate = campaign.additionalRequirementsTemplate;

    const isCompactView = view === 'compact';

    const classes = classnames({
      'campaign-card': true,
      'campaign-card--collapsed': isExpandable && isCollapsed,
      'campaign-card--compact': view === 'compact'
    });

    const campaignCardContentClasses = classnames(
      'campaign-card__content', {
        'campaign-card__content--expandable': isExpandable
      }
    );

    const contentColumnClasses = classnames({
      'campaign-card__content-column': true,
      [`campaign-card__content-column--layout-${layout}`]: layout
    });

    return (
      <PageSection
        style={isCompactView ? 'none' : 'volume'}
        margin="none"
        border="none"
        padding="none"
      >
        <div className={classes}>
          <div ref={this.campaignCardContentRef}>
            {this.renderHeader()}
            <div className={campaignCardContentClasses}>
              <div className={contentColumnClasses}>
                <div className="campaign-card__description">
                  <div className="campaign-card__subtitle">
                    <span>{__t('campaign_card.campaign_description_label')}</span>
                  </div>
                  <div className="text-preserve-whitespaces text-overflow-wrap">
                    <RichTextEditor
                      key={campaignDescription}
                      canEdit={false}
                      value={campaignDescription}
                    />
                  </div>
                  {
                    campaign.managers && campaign.managers.length > 0 &&
                      <div className="campaign-card__text">
                        <div className="campaign-card__subtitle">
                          <span>{__t('campaign_card.managers_label')}</span>
                        </div>
                        {getCampaignManagersPreview(campaign.managers)}
                      </div>
                  }
                </div>
                {
                  inspirationalMaterial.length > 0 && (
                    <div className="campaign-card__inspiration">
                      <div className="campaign-card__subtitle">
                        <span>{__t('campaign_card.inspirational_material_label')}</span>
                      </div>
                      <div className="campaign-card__inspiration-items">
                        {
                          inspirationalMaterial.map((asset, index) => (
                            <DivWithModal
                              modalContentProps={{
                                items: galleryItems,
                                currentPosition: index
                              }}
                              key={`inspirational-material-${asset.id}`}
                            >
                              <MediaPreview
                                attachment={prepareCampaignAssetForMediaPreview(asset)}
                                width={124}
                                height={124}
                              />
                            </DivWithModal>
                          ))
                        }
                      </div>
                    </div>
                  )
                }
              </div>
              {
                !isEmptyEditorText(campaignAdditionalRequirementsTemplate) &&
                  <div className={contentColumnClasses}>
                    <div className="campaign-card__additional-requirements">
                      <div className="campaign-card__subtitle">
                        <span>{__t('campaign_card.additional_requirements_label')}</span>
                      </div>
                      <div className="text-preserve-whitespaces text-overflow-wrap">
                        <RichTextEditor
                          key={campaignAdditionalRequirementsTemplate}
                          canEdit={false}
                          value={campaignAdditionalRequirementsTemplate}
                        />
                      </div>
                    </div>
                  </div>
              }
            </div>
            { !isCompactView && this.state.isExpandable &&
              <div className="campaign-card__action">
                <ActionLink
                  onClick={this.toggleCollapsed}
                  color="secondary"
                  size="inherit"
                >
                  {__t(isCollapsed ? 'campaign_card.show_more_label' : 'campaign_card.show_less_label')}
                </ActionLink>
              </div>}
          </div>
        </div>
      </PageSection>
    );
  }
}

export default CampaignCard;
